import React, { useCallback, useEffect, useId, useState } from 'react';
import { Edit, Mail, Plus, Trash2 } from 'lucide-react';
import Dialog from '../../common/Dialog';
import { ConfirmDialog, useToast } from '../../common';
import { LegalGuardiansService } from '../../../services/patientmr/legalGuardians.service';
import { LEGAL_AUTHORITY_OPTIONS } from '../../../constants/patientLifecycle';
import { notifyPatientChartUpdated } from '../../../constants/navigationEvents';
import type { LegalAuthority, LegalGuardian, LegalGuardianInput } from '../../../dtos/patientmr/legalGuardian.dto';

export interface GuardiansSectionProps {
  patientId: string;
  readOnly: boolean;
}

const emptyInput = (contactOrder: number): LegalGuardianInput => ({
  name: '',
  relationship: '',
  legalAuthority: 'PARENT',
  phone: '',
  email: '',
  contactOrder,
  canConsent: true,
  isResponsibleAdult: true,
});

const authorityLabel = (value: LegalAuthority) => LEGAL_AUTHORITY_OPTIONS.find((o) => o.value === value)?.label ?? value;

const inputClass = 'mt-1 w-full rounded-md border border-slate-300 p-2 text-sm bg-white';

/** Required fields for a guardian row (§8: name, relationship, legalAuthority, phone). */
export function validateGuardian(input: LegalGuardianInput): string | null {
  if (!input.name.trim() || !input.relationship.trim() || !input.legalAuthority || !input.phone.trim()) {
    return 'Name, relationship, legal authority and phone are required.';
  }
  if (input.email && input.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.email.trim())) {
    return 'Enter a valid email address or leave it blank.';
  }
  if (!Number.isInteger(input.contactOrder) || input.contactOrder < 1) {
    return 'Contact order must be 1 or higher.';
  }
  return null;
}

/**
 * Guardians & legal representatives (PAT-02). Legal authority records, separate from the
 * family-portal accounts (EX-12): a guardian without an email can't be invited to the portal.
 */
const GuardiansSection: React.FC<GuardiansSectionProps> = ({ patientId, readOnly }) => {
  const baseId = useId();
  const { showToast } = useToast();
  const [guardians, setGuardians] = useState<LegalGuardian[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string | null; input: LegalGuardianInput } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<LegalGuardian | null>(null);

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setLoadError(null);
    try {
      setGuardians(await LegalGuardiansService.list(patientId));
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Unable to load guardians.');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    void load();
  }, [load]);

  const openAdd = () => {
    const nextOrder = guardians.reduce((max, g) => Math.max(max, g.contactOrder || 0), 0) + 1;
    setFormError(null);
    setEditing({ id: null, input: emptyInput(nextOrder) });
  };

  const openEdit = (g: LegalGuardian) => {
    setFormError(null);
    setEditing({
      id: g.guardianId,
      input: {
        name: g.name,
        relationship: g.relationship,
        legalAuthority: g.legalAuthority,
        phone: g.phone,
        email: g.email ?? '',
        contactOrder: g.contactOrder,
        canConsent: g.canConsent,
        isResponsibleAdult: g.isResponsibleAdult,
      },
    });
  };

  const update = <K extends keyof LegalGuardianInput>(key: K, value: LegalGuardianInput[K]) =>
    setEditing((prev) => (prev ? { ...prev, input: { ...prev.input, [key]: value } } : prev));

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editing) return;
    const validation = validateGuardian(editing.input);
    if (validation) {
      setFormError(validation);
      return;
    }
    const payload: LegalGuardianInput = {
      ...editing.input,
      name: editing.input.name.trim(),
      relationship: editing.input.relationship.trim(),
      phone: editing.input.phone.trim(),
      email: editing.input.email?.trim() ? editing.input.email.trim() : null,
    };
    setSaving(true);
    setFormError(null);
    try {
      if (editing.id) {
        await LegalGuardiansService.update(patientId, editing.id, payload);
      } else {
        await LegalGuardiansService.create(patientId, payload);
      }
      setEditing(null);
      showToast('Guardian saved.', { variant: 'success' });
      notifyPatientChartUpdated(patientId);
      await load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Unable to save the guardian.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setSaving(true);
    try {
      await LegalGuardiansService.remove(patientId, deleting.guardianId);
      setDeleting(null);
      showToast('Guardian removed.', { variant: 'success' });
      notifyPatientChartUpdated(patientId);
      await load();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Unable to remove the guardian.', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const id = (name: string) => `${baseId}-${name}`;

  return (
    <div className="text-left">
      {loading && <p className="text-sm text-slate-500">Loading guardians…</p>}
      {loadError && (
        <p role="alert" className="text-sm text-red-700">
          {loadError}{' '}
          <button type="button" className="underline" onClick={() => void load()}>
            Try again
          </button>
        </p>
      )}
      {!loading && !loadError && guardians.length === 0 && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
          No guardians or legal representatives recorded. Patients under 18 need a guardian who can consent before admission.
        </p>
      )}
      {guardians.length > 0 && (
        <ul className="divide-y divide-slate-200 rounded-md border border-slate-200" aria-label="Guardians and legal representatives">
          {guardians.map((g) => (
            <li key={g.guardianId} className="flex flex-wrap items-start justify-between gap-2 p-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800">
                  <span className="mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-200 text-xs text-slate-700" title="Contact order">
                    {g.contactOrder}
                  </span>
                  {g.name} <span className="font-normal text-slate-500">· {g.relationship}</span>
                </p>
                <p className="mt-0.5 text-xs text-slate-600">
                  {authorityLabel(g.legalAuthority)} · {g.phone}
                  {g.email ? ` · ${g.email}` : ''}
                </p>
                <div className="mt-1 flex flex-wrap gap-1.5 text-[11px]">
                  {g.canConsent && <span className="rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-emerald-800">Can consent</span>}
                  {g.isResponsibleAdult && (
                    <span className="rounded border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-sky-800">Responsible adult</span>
                  )}
                  {!g.email && (
                    <span className="inline-flex items-center gap-1 rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-slate-600">
                      <Mail size={11} aria-hidden="true" /> Portal invite unavailable — no email
                    </span>
                  )}
                </div>
              </div>
              {!readOnly && (
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => openEdit(g)}
                    className="rounded p-2 text-blue-600 hover:bg-blue-50"
                    aria-label={`Edit guardian ${g.contactOrder}`}
                  >
                    <Edit size={14} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleting(g)}
                    className="rounded p-2 text-red-600 hover:bg-red-50"
                    aria-label={`Remove guardian ${g.contactOrder}`}
                  >
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {!readOnly && (
        <button
          type="button"
          onClick={openAdd}
          className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-800 hover:bg-slate-50"
        >
          <Plus size={14} aria-hidden="true" /> Add guardian
        </button>
      )}

      <Dialog open={!!editing} onClose={() => !saving && setEditing(null)} labelledBy={id('title')}>
        {editing && (
          <form onSubmit={save} className="text-left" noValidate>
            <h3 id={id('title')} className="text-lg font-semibold text-slate-800 pr-6">
              {editing.id ? 'Edit guardian' : 'Add guardian'}
            </h3>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label htmlFor={id('name')} className="block text-sm font-medium text-slate-700">Name <span className="text-red-600" aria-hidden="true">*</span></label>
                <input id={id('name')} value={editing.input.name} onChange={(e) => update('name', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label htmlFor={id('rel')} className="block text-sm font-medium text-slate-700">Relationship <span className="text-red-600" aria-hidden="true">*</span></label>
                <input id={id('rel')} value={editing.input.relationship} onChange={(e) => update('relationship', e.target.value)} className={inputClass} placeholder="e.g. Mother" />
              </div>
              <div>
                <label htmlFor={id('auth')} className="block text-sm font-medium text-slate-700">Legal authority <span className="text-red-600" aria-hidden="true">*</span></label>
                <select id={id('auth')} value={editing.input.legalAuthority} onChange={(e) => update('legalAuthority', e.target.value as LegalAuthority)} className={inputClass}>
                  {LEGAL_AUTHORITY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor={id('phone')} className="block text-sm font-medium text-slate-700">Phone <span className="text-red-600" aria-hidden="true">*</span></label>
                <input id={id('phone')} type="tel" value={editing.input.phone} onChange={(e) => update('phone', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label htmlFor={id('email')} className="block text-sm font-medium text-slate-700">Email</label>
                <input id={id('email')} type="email" value={editing.input.email ?? ''} onChange={(e) => update('email', e.target.value)} className={inputClass} placeholder="Optional" />
              </div>
              <div>
                <label htmlFor={id('order')} className="block text-sm font-medium text-slate-700">Contact order</label>
                <input
                  id={id('order')}
                  type="number"
                  min={1}
                  value={editing.input.contactOrder}
                  onChange={(e) => update('contactOrder', Number(e.target.value))}
                  className={inputClass}
                />
              </div>
              <div className="sm:col-span-2 flex flex-col gap-2 text-sm text-slate-700">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={editing.input.canConsent} onChange={(e) => update('canConsent', e.target.checked)} />
                  Can consent
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={editing.input.isResponsibleAdult} onChange={(e) => update('isResponsibleAdult', e.target.checked)} />
                  Responsible adult (trained in care)
                </label>
              </div>
            </div>
            {!editing.input.email?.trim() && (
              <p className="mt-2 text-xs text-slate-500">Without an email this guardian can't be invited to the family portal.</p>
            )}
            {formError && (
              <p role="alert" className="mt-3 text-sm text-red-700">
                {formError}
              </p>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setEditing(null)} disabled={saving} className="px-4 py-2 rounded-md text-sm border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-60">
                Cancel
              </button>
              <button type="submit" disabled={saving} className="px-4 py-2 rounded-md text-sm bg-slate-800 hover:bg-slate-700 text-white disabled:opacity-50">
                {saving ? 'Saving…' : 'Save guardian'}
              </button>
            </div>
          </form>
        )}
      </Dialog>

      <ConfirmDialog
        open={!!deleting}
        title="Remove guardian"
        message="Remove this guardian or legal representative from the chart?"
        confirmLabel="Remove"
        destructive
        busy={saving}
        onConfirm={confirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
};

export default GuardiansSection;
